import { Link } from "react-router-dom";
import { ArrowRight, Camera, Sparkles, GraduationCap, Briefcase, CalendarCheck, BookOpen, MapPin, Phone, Mail } from "lucide-react";
import { Logo } from "../components/Brand";
import { useAuth, homeFor } from "../context/AuthContext";
import { useTrainings, HeroPromo, TrainingCard } from "../components/Trainings";
import TrainingRequestForm from "../components/TrainingRequestForm";
import PromoVideo from "../components/PromoVideo";
import { ConsultServices, CasesTeam, Testimonials, ConsultContact } from "../components/ConsultSections";
import HeroVideo from "../components/HeroVideo";
import { cedi as ghs } from "../lib/api";
import { CountryPicker } from "../components/CurrencyGate";

const BELT_IMG = ["/brand/belt_yellow.jpg", "/brand/belt_green.jpg", "/brand/belt_black.jpg"];

const BELTS = [
  ["Yellow Belt", "CSSYB", "2-day foundation in Lean Six Sigma and DMAIC.", "#F3C623", 500, "yellow-belt", null],
  ["Green Belt", "CSSGB", "5-day course to lead or support improvement projects.", "#22c55e", 1000, "green-belt", "Yellow Belt"],
  ["Black Belt", "CSSBB", "12-week accelerated programme with exam prep and coaching.", "#111827", 1500, "black-belt", "Green Belt"],
];

const EnrolButton = ({ slug, name }) => {
  const { user } = useAuth();
  const academy = `/app/academy?enrol=${slug}`;
  const [to, label] = !user ? [`/register?role=staff&next=${encodeURIComponent(academy)}`, `Enrol in ${name}`]
    : user.role === "staff" ? [academy, `Enrol in ${name}`]
    : user.role === "employer" ? ["/app/train-staff", "Train your staff"]
    : ["/app/admin/content", "Manage course"];
  return <Link to={to} className="dx-btn-gold w-full mt-5" data-testid={`landing-enrol-${slug}-btn`}>{label} <ArrowRight className="w-4 h-4" /></Link>;
};

const FEATURES = [
  [Sparkles, "Automatch", "Employers post a job and we rank the best-fit candidates by skills, certification, location and experience."],
  [Camera, "Photo clock-ins", "Clock in, clock out and breaks are recorded with a photo, time and location as evidence."],
  [CalendarCheck, "Task schedules", "Employers assign daily tasks and staff update progress from their phones."],
  [BookOpen, "Staff book", "Automatic timesheets with hours worked and break time for every staff member."],
];

const Nav = () => {
  const { user } = useAuth();
  return (
    <header className="absolute top-0 inset-x-0 z-20 px-5 sm:px-10 py-5 flex items-center justify-between">
      <Logo light />
      <div className="flex items-center gap-2">
        <span className="hidden sm:inline-flex mr-2"><CountryPicker light testid="nav-country-picker" /></span>
        <a href="#train-your-team" className="hidden md:inline-flex text-white/85 hover:text-white text-sm font-semibold px-3" data-testid="nav-train-team-link">Train your team</a>
        {user ? <Link to={homeFor(user.role)} className="dx-btn-gold" data-testid="nav-dashboard-btn">Dashboard</Link> : <>
          <Link to="/login" className="hidden sm:inline-flex text-white/85 hover:text-white text-sm font-semibold px-4" data-testid="nav-login-btn">Log in</Link>
          <Link to="/register" className="dx-btn-gold whitespace-nowrap px-4 sm:px-5" data-testid="nav-register-btn">Join now</Link>
        </>}
      </div>
    </header>
  );
};

const Hero = ({ trainings }) => (
  <section className="relative bg-[#14081C] text-white overflow-hidden min-h-[100svh] flex flex-col" data-testid="hero">
    <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 75% 45%, #2A1236 0%, #14081C 65%)" }} />
    <HeroVideo />
    <Nav />
    <div className="relative flex-1 w-full max-w-7xl mx-auto px-5 sm:px-10 pt-32 pb-12 lg:pt-36 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <div className="lg:col-span-7">
        <div className="flex items-center gap-3 dx-rise"><span className="h-px w-10 bg-[#D4AF37]" /><span className="dx-eyebrow">Dexterity Consult · Accra, Ghana</span></div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-[-0.03em] leading-[0.98] mt-6 dx-rise dx-d1 uppercase">
          Train.<br />Certify.<br /><span className="text-[#D4AF37]">Get hired.</span>
        </h1>
        <p className="text-base md:text-lg text-white/80 mt-7 max-w-xl dx-rise dx-d2">
          Recruitment agency and Lean Six Sigma training house - headquartered in Accra, open to talent across Africa and the world. Join for {ghs(100)}, let us polish your resume, earn your belts and get matched to employers who need you.
        </p>
        <div className="flex flex-wrap gap-3 mt-9 dx-rise dx-d3">
          <Link to="/register?role=staff" className="dx-btn-gold px-7 py-3.5" data-testid="hero-staff-cta">I'm looking for work <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/register?role=employer" className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold border border-white/30 hover:bg-white/10 transition-colors" data-testid="hero-employer-cta">I'm hiring staff</Link>
          <PromoVideo />
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 border border-white/15 border-t-2 border-t-[#D4AF37] dx-rise dx-d4" data-testid="hero-pricing">
          {[["Membership", 100, "#D4AF37"], ...BELTS.map(([name, , , c, price]) => [name, price, c === "#111827" ? "#fff" : c])].map(([name, price, c]) => (
            <div key={name} className="p-4 border-white/10 border-r border-b sm:border-b-0 last:border-r-0">
              <div className="flex items-center gap-2 text-xs text-white/70"><span className="w-1 h-3.5" style={{ background: c }} />{name}</div>
              <div className="font-display text-xl font-bold mt-1">{ghs(price)}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/50 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">Membership includes a professional resume tidy-up. Belt fees include exam and certificate. <span className="sm:hidden"><CountryPicker light testid="hero-country-picker" /></span></p>
      </div>
    </div>
    <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-10 pb-10 dx-rise dx-d4"><HeroPromo trainings={trainings} /></div>
  </section>
);

const Academy = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28">
    <div className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <img src="/brand/logo.png" alt="Dexterity Consult" className="w-20 h-20 object-contain" />
        <div className="dx-eyebrow mt-6">Dexterity Consult Academy</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#250E31]">Lean Six Sigma, one belt at a time</h2>
        <p className="text-[#6b5a73] mt-4">Training aligned with the American Society for Quality (ASQ) Body of Knowledge. Pay for each certification as you progress, learn on your phone and pass the exam to earn a verifiable certificate. Exam and certificate are included in every belt fee.</p>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
        {BELTS.map(([name, code, text, c, price, slug, prereq], i) => (
          <div key={code} className="dx-card overflow-hidden hover:-translate-y-1 transition-transform duration-300 flex flex-col" data-testid={`landing-belt-${code.toLowerCase()}`}>
            <div className="relative">
              <img src={BELT_IMG[i]} alt={`${name} training`} className="w-full aspect-[4/3] object-cover" />
              <div className="absolute bottom-0 inset-x-0 h-1.5" style={{ background: c }} />
            </div>
            <div className="p-6 flex-1 flex flex-col">
            <div className="text-xs text-[#8a7a92]">Level {i + 1} · {code}</div>
            <h3 className="font-display text-xl font-bold mt-1 text-[#250E31]">{name}</h3>
            <p className="text-sm text-[#6b5a73] mt-3 flex-1">{text}</p>
            <div className="font-display text-2xl font-bold text-[#5B2A6E] mt-6">{ghs(price)}</div>
            <div className="text-xs text-[#8a7a92]">Includes exam & certificate</div>
            <div className="text-[11px] text-[#8a7a92] mt-1" data-testid={`landing-belt-${slug}-req`}>{prereq ? `Requires ${prereq} + membership` : `Requires ${ghs(100)} membership`}</div>
            <EnrolButton slug={slug} name={name} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Upcoming = ({ trainings }) => (
  <section id="upcoming" className="max-w-7xl mx-auto px-5 sm:px-10 pb-20 lg:pb-28" data-testid="upcoming-section">
    <div className="flex items-center gap-3"><span className="h-px w-10 bg-[#D4AF37]" /><span className="dx-eyebrow">Upcoming trainings</span></div>
    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#250E31]">Next intakes</h2>
    {trainings.length ? (
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{trainings.map((t) => <TrainingCard key={t.id} t={t} />)}</div>
    ) : (
      <div className="mt-8 dx-card p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="upcoming-empty">
        <p className="text-[#4b3d52]">New Yellow, Green and Black Belt dates are being scheduled. Join now and you'll be first to enrol.</p>
        <Link to="/register?role=staff" className="dx-btn-purple shrink-0">Join for {ghs(100)}</Link>
      </div>
    )}
  </section>
);

const TrainTeam = () => (
  <section id="train-your-team" className="bg-[#14081C] text-white" data-testid="train-team-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <img src="/brand/team_training.jpg" alt="Corporate Lean Six Sigma training" className="w-full aspect-[16/10] object-cover border-t-2 border-[#D4AF37]" />
        <div className="flex items-center gap-3 mt-8"><span className="h-px w-10 bg-[#D4AF37]" /><span className="dx-eyebrow">For companies</span></div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3">Train your staff with Dexterity Consult</h2>
        <p className="text-white/70 mt-4">Book Lean Six Sigma Yellow, Green or Black Belt training for your team - at your premises, in our classroom or as live virtual sessions. Tell us what you need and we'll send a proposal.</p>
      </div>
      <div className="lg:col-span-7 bg-white/[0.04] border border-white/10 p-6 sm:p-8">
        <TrainingRequestForm dark />
      </div>
    </div>
  </section>
);

const Platform = () => (
  <section className="bg-[#FAF8FC] border-y">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
      <div className="relative">
        <img src="/brand/training_cssbb.jpg" alt="Dexterity Consult Black Belt training session" className="rounded-3xl w-full aspect-[4/3] object-cover" />
        <div className="absolute -bottom-6 -right-2 sm:right-6 bg-[#5B2A6E] text-white rounded-2xl p-5 max-w-[240px]">
          <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
          <p className="text-sm mt-2">"Black Belts don't wait. They lead."</p>
        </div>
      </div>
      <div>
        <div className="dx-eyebrow">One platform for staff & employers</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#250E31]">Recruitment, training and workforce management</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-9">
          {FEATURES.map(([Icon, title, text]) => (
            <div key={title}>
              <div className="w-10 h-10 rounded-xl bg-[#5B2A6E] grid place-items-center"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
              <h3 className="font-display font-semibold text-lg mt-4 text-[#250E31]">{title}</h3>
              <p className="text-sm text-[#6b5a73] mt-1.5">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28">
    <div className="grid md:grid-cols-2 gap-6">
      {[["For staff", [`Join for ${ghs(100)} by mobile money or card and upload your resume`, "We tidy up your resume professionally", "Earn Yellow, Green and Black Belts - your resume updates itself", "Get matched and hired, then clock in from your phone"], "/register?role=staff", "Start as staff", "staff"],
        ["For employers", ["Register your company", "Post a job with skills and certification level", "Review automatched candidates and shortlist", "Hire, assign tasks and track attendance with photos"], "/register?role=employer", "Start hiring", "employer"]].map(([title, steps, to, cta, k], i) => (
        <div key={k} className={`rounded-3xl p-8 sm:p-10 ${i ? "bg-[#250E31] text-white" : "dx-card"}`}>
          <Briefcase className="w-6 h-6 text-[#D4AF37]" />
          <h3 className="font-display text-2xl font-bold mt-4">{title}</h3>
          <ol className="mt-6 space-y-4">
            {steps.map((s, n) => (
              <li key={s} className="flex gap-4"><span className="font-display font-bold text-[#D4AF37]">0{n + 1}</span><span className={i ? "text-white/80" : "text-[#4b3d52]"}>{s}</span></li>
            ))}
          </ol>
          <Link to={to} className={`${i ? "dx-btn-gold" : "dx-btn-purple"} mt-8`} data-testid={`steps-${k}-cta`}>{cta} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ))}
    </div>
  </section>
);

const PARTNERS = [
  ["/brand/partners/asq_cssbb.png", "ASQ Certified Six Sigma Black Belt"],
  ["/brand/partners/ilssi.png", "International Lean Six Sigma Institute - Certified Black Belt"],
  ["/brand/partners/asq.png", "ASQ - American Society for Quality"],
  ["/brand/partners/lean6s.png", "Lean Six Sigma"],
  ["/brand/partners/partner_crest_1.png", "Partner institution"],
  ["/brand/partners/partner_crest_2.png", "Partner institution"],
];

const PartnersBanner = () => (
  <section className="bg-white border-t" data-testid="partners-banner">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-12 flex flex-col lg:flex-row lg:items-center gap-8">
      <div className="lg:w-64 shrink-0">
        <div className="dx-eyebrow">Accreditation & partners</div>
        <p className="text-sm text-[#6b5a73] mt-2">Certification pathways aligned with ASQ and ILSSI, with training across accredited bodies including ASQ, CSSC, BQF and IASSC.</p>
      </div>
      <div className="flex-1 flex flex-wrap items-center justify-center lg:justify-between gap-6 sm:gap-10">
        {PARTNERS.map(([src, alt]) => (
          <img key={src} src={src} alt={alt} title={alt} className="h-16 sm:h-20 w-auto object-contain" data-testid="partner-logo" />
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#250E31] text-white">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-14 grid md:grid-cols-3 gap-10">
      <div><Logo light /><p className="text-sm text-white/60 mt-4">Consultants · Engineers · Strategists</p></div>
      <div className="space-y-2 text-sm text-white/75">
        <div className="flex gap-2"><MapPin className="w-4 h-4 text-[#D4AF37]" />One Airport Square, Plot 21, Accra</div>
        <div className="flex gap-2"><Phone className="w-4 h-4 text-[#D4AF37]" />+233 20 674 8385 · +233 20 267 1766</div>
        <div className="flex gap-2"><Mail className="w-4 h-4 text-[#D4AF37]" />training@dexterityinstitute.com</div>
        <ConsultContact />
      </div>
      <div className="text-sm text-white/75 space-y-2">
        <a href="https://www.dexterityconsultgh.com" target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]" data-testid="footer-website-link">www.dexterityconsultgh.com</a>
        <div className="text-white/40 text-xs pt-4">© {new Date().getFullYear()} Dexterity Consult. All rights reserved.</div>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  const trainings = useTrainings();
  return (
    <div data-testid="landing-page">
      <Hero trainings={trainings} />
      <Academy />
      <Upcoming trainings={trainings} />
      <TrainTeam />
      <Platform />
      <ConsultServices />
      <CasesTeam />
      <Testimonials />
      <Steps />
      <PartnersBanner />
      <Footer />
    </div>
  );
}
